import { useState, useEffect } from 'react';

export interface DrugOption {
  label: string;
  value: string;
}

export interface IndicationOption {
  label: string;
  value: string;
}

export interface TherapyOption {
  label: string;
  value: string;
  indications: IndicationOption[];
}

export interface CancerType {
  label: string;
  value: string;
  therapies: TherapyOption[];
}

export interface PrimaryDrugFull {
  label: string;
  value: string;
  cancerType: CancerType[];
  secondaryDrugs: DrugOption[];
}

export interface TherapyDrugsResponse {
  primaryDrugs: PrimaryDrugFull[];
}

interface UseTherapyDrugsReturn {
  primaryDrugs: DrugOption[]; // Simplified for dropdown
  primaryDrugsFull: PrimaryDrugFull[]; // Full structure with cancer types, therapies, etc.
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  getSecondaryDrugsForPrimary: (primaryDrugValue: string) => DrugOption[];
  getCancerTypesForPrimary: (primaryDrugValue: string) => CancerType[];
}

const useTherapyDrugs = (): UseTherapyDrugsReturn => {
  const [primaryDrugsFull, setPrimaryDrugsFull] = useState<PrimaryDrugFull[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTherapyDrugs = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const secretKey = process.env.REACT_APP_HEALTH_SECRET_KEY || '';
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/api/v8/healthcoach/therapy_drugs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'token': `${token}`,
          'health_secret': secretKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch therapy drugs: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.code === 1 && data.data && data.data.primaryDrugs) {
        setPrimaryDrugsFull(data.data.primaryDrugs);
      } else {
        throw new Error(data.message || 'Failed to fetch therapy drugs');
      }
    } catch (err: any) {
      console.error('Error fetching therapy drugs:', err);
      setError(err.message || 'An error occurred while fetching therapy drugs');
      // Set fallback empty array to prevent app crashes
      setPrimaryDrugsFull([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTherapyDrugs();
  }, []);

  const refetch = () => {
    fetchTherapyDrugs();
  };

  // Get simplified primary drugs array for dropdown
  const primaryDrugs: DrugOption[] = primaryDrugsFull.map(drug => ({
    label: drug.label,
    value: drug.value,
  }));

  // Get secondary drugs for a specific primary drug
  const getSecondaryDrugsForPrimary = (primaryDrugValue: string): DrugOption[] => {
    const primaryDrug = primaryDrugsFull.find(drug => drug.value === primaryDrugValue);
    return primaryDrug?.secondaryDrugs || [];
  };

  // Get cancer types for a specific primary drug
  const getCancerTypesForPrimary = (primaryDrugValue: string): CancerType[] => {
    const primaryDrug = primaryDrugsFull.find(drug => drug.value === primaryDrugValue);
    return primaryDrug?.cancerType || [];
  };

  return {
    primaryDrugs,
    primaryDrugsFull,
    isLoading,
    error,
    refetch,
    getSecondaryDrugsForPrimary,
    getCancerTypesForPrimary,
  };
};

export default useTherapyDrugs;

